import {
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  Link,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import {
  FaBell,
  FaCalendarCheck,
  FaCreditCard,
  FaEdit,
  FaUserCheck,
} from "react-icons/fa";
import { IoMdNotificationsOutline } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useGetRecentActivitiesQuery } from "../features/activityApi";

interface RecentActivityProps {
  memberId?: string;
  limit?: number;
  showTitle?: boolean;
  isAdminView?: boolean;
  isViewAll?: boolean;
}

const getActivityIcon = (activityType: string) => {
  switch (activityType) {
    case "profile_update":
      return FaEdit;
    case "event_registration":
      return FaCalendarCheck;
    case "payment":
      return FaCreditCard;
    case "status_change":
      return FaUserCheck;
    default:
      return FaBell;
  }
};

const getActivityColor = (activityType: string) => {
  switch (activityType) {
    case "profile_update":
      return "blue";
    case "event_registration":
      return "green";
    case "payment":
      return "purple";
    case "status_change":
      return "orange";
    default:
      return "gray";
  }
};

const RecentActivity: React.FC<RecentActivityProps> = ({
  memberId,
  limit = 5,
  showTitle = true,
  isAdminView = false,
  isViewAll = true,
}) => {
  const {
    data: activities,
    isLoading,
    error,
  } = useGetRecentActivitiesQuery({
    memberId,
    limit,
  });

  const navigate = useNavigate();

  const { roles } = useAuth();
  
  const path = roles?.includes("ADMIN")
    ? "/admin/dashboard"
    : "/member/dashboard";

  if (isLoading) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="lg" />
        <Text mt={2}>Loading recent activities...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} textAlign="center">
        <Text color="red.500">Failed to load recent activities</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack mb={3} justify="space-between" align="center">
        {showTitle && (
          <Heading fontSize="lg" fontWeight="bold" color="gray.800">
            Recent Activity
          </Heading>
        )}
        {isViewAll && <Link
          onClick={() => {
            navigate(`${path}/activities`);
          }}
          colorPalette={"blue"}
          fontSize={"xs"}
          fontWeight={"semibold"}
        >
          View All
        </Link>}
      </HStack>

      {!activities || activities.length === 0 ? (
        <Card.Root bg="white">
          <Card.Body py={8} textAlign="center">
            <VStack gap={3}>
              <Icon as={FaBell} color="gray.400" boxSize={8} />
              <Text color="gray.500" fontSize="lg">
                No recent activities
              </Text>
              <Text color="gray.400" fontSize="sm">
                {memberId
                  ? "This member has no recent activities"
                  : "No activities to display"}
              </Text>
            </VStack>
          </Card.Body>
        </Card.Root>
      ) : (
        <VStack gap={3} align="stretch">
          {activities.map((activity) => {
            const IconComponent = getActivityIcon(activity.activityType);
            const colorScheme = getActivityColor(activity.activityType);

            return (
              <Card.Root key={activity.id} bg="white">
                <Card.Body p={4}>
                  <Flex gap={4}>
                    <Box>
                      <Icon
                        as={IoMdNotificationsOutline}
                        color={`${colorScheme}.500`}
                        boxSize={7}
                        mt={1}
                      />
                    </Box>

                    <Box flex="1">
                      <VStack align="start" gap={1}>
                        <HStack justify="space-between" w="100%">
                          <Heading
                            fontWeight="semibold"
                            color="gray.800"
                            size="sm"
                          >
                            {activity.title}
                          </Heading>
                          <Text fontSize="xs" color="gray.500">
                            {formatDistanceToNow(new Date(activity.createdAt), {
                              addSuffix: true,
                            })}
                          </Text>
                        </HStack>

                        <HStack>
                          <Text color="gray.600" fontSize="xs" lineHeight="1.5">
                            {isAdminView
                              ? activity.adminDescription ||
                                activity.memberDescription
                              : activity.memberDescription ||
                                activity.adminDescription}
                          </Text>
                        </HStack>
                      </VStack>
                    </Box>
                  </Flex>
                </Card.Body>
              </Card.Root>
            );
          })}
        </VStack>
      )}
    </Box>
  );
};

export default RecentActivity;
